import * as React from "react";
import { Columns3Icon, Rows3Icon } from "lucide-react";

import { cn } from "@/lib/utils";

import { Kbd } from "./kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

type HomeLayout = "vertical" | "horizontal";

type LayoutToggleProps = {
  value: HomeLayout;
  onValueChange: (next: HomeLayout) => void;
  /** Bind ⌘B (Ctrl+B) while mounted. */
  shortcut?: boolean;
  className?: string;
};

const OPTIONS: { value: HomeLayout; label: string; icon: typeof Rows3Icon }[] =
  [
    { value: "vertical", label: "Vertical", icon: Rows3Icon },
    { value: "horizontal", label: "Horizontal", icon: Columns3Icon }
  ];

/** The Vertical / Horizontal segment beside Display (⌘B toggles). */
function LayoutToggle({
  value,
  onValueChange,
  shortcut = true,
  className
}: LayoutToggleProps) {
  React.useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        !e.shiftKey &&
        e.key.toLowerCase() === "b"
      ) {
        e.preventDefault();
        onValueChange(value === "vertical" ? "horizontal" : "vertical");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcut, value, onValueChange]);

  return (
    <div
      role="radiogroup"
      aria-label="Layout"
      data-slot="layout-toggle"
      className={cn(
        "inline-flex h-7 items-center gap-0.5 rounded-md bg-fill-1 p-0.5",
        className
      )}
    >
      {OPTIONS.map(({ value: v, label, icon: Icon }) => (
        <Tooltip key={v}>
          <TooltipTrigger asChild>
            <button
              type="button"
              role="radio"
              aria-checked={value === v}
              aria-label={label}
              onClick={() => onValueChange(v)}
              className="flex size-6 items-center justify-center rounded-sm text-fg-3 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 aria-checked:bg-elevated aria-checked:text-fg aria-checked:ring-1 aria-checked:ring-line-2"
            >
              <Icon className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {label} <Kbd>⌘B</Kbd>
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

export { LayoutToggle };
export type { HomeLayout, LayoutToggleProps };
